// Platform-console audit-access management for a tenant. Backs the "Audit access"
// panel on the tenant detail page. Talks to the auth-service platform surface
// (/platform/rbac/tenants/:tenantId/audit-access), which is whitelisted to the
// audit role family and gated on tenant:read:all / tenant:update:all.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

export interface AuditRole {
  key: 'auditor' | 'compliance_officer' | 'audit_administrator';
  label: string;
  description: string | null;
  permissions: string[];
}

export interface AuditHolder {
  userId: string;
  email: string | null;
  fullName: string | null;
  roles: string[];
}

export interface AuditUser {
  id: string;
  email: string | null;
  fullName: string | null;
  auditRoles: string[];
}

const base = (tenantId: string) => `${API_V1}/platform/rbac/tenants/${tenantId}/audit-access`;

export const getAssignableAuditRoles = (tenantId: string) =>
  axios.get<{ roles: AuditRole[] }>(`${base(tenantId)}/assignable-roles`).then((r) => r.data.roles);

export const getAuditHolders = (tenantId: string) =>
  axios
    .get<{ tenant: { id: string; name: string; slug: string }; holders: AuditHolder[] }>(base(tenantId))
    .then((r) => r.data);

export const searchTenantUsers = (tenantId: string, q?: string) =>
  axios
    .get<{ users: AuditUser[] }>(`${base(tenantId)}/users`, { params: q ? { q } : {} })
    .then((r) => r.data.users);

export const grantAuditRole = (tenantId: string, userId: string, roleKey: string) =>
  axios
    .post<{ created: boolean }>(base(tenantId), { userId, roleKey })
    .then((r) => r.data);

export const revokeAuditRole = (tenantId: string, userId: string, roleKey: string) =>
  axios.delete(`${base(tenantId)}/${userId}/${roleKey}`).then((r) => r.data);
